import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, ArrowRight, Trash2, Download, CheckCircle, XCircle,
  ChevronDown, ChevronRight, FileText, Truck, Loader2, ShieldCheck,
  BarChart3, Activity, AlertTriangle, TrendingUp, Gauge, Layers,
  Printer, Wrench, Clock, Grid3X3, Upload, Pencil, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import ProjectFormModal from '../components/ProjectFormModal';
import { getDirectoryHandle, listFiles } from '../utils/workingDirectory';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Area, AreaChart,
  ReferenceLine
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  editProject: "https://customer-assets.emergentagent.com/job_9a1b182f-d27c-4202-90ce-7b91b29a5441/artifacts/jo09qaga_EditProject.png",
  delivery: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/ekpk2yty_Delivery.png",
  dmlBuilder: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/rtc93b69_DML%20Builder%20Logo2.png",
  brexValidation: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/hd7tcvfm_BREXValidation.png",
  brexBuilder: "https://customer-assets.emergentagent.com/job_4dda6c2c-c6cf-494a-b578-b0548b3af0ae/artifacts/d5198eca_BREXBuilder.png",
  author: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/9fs2w7fm_Author.png",
  pdfPublish: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/49fa80il_PDFPublish.png",
  steCheck: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/vfvqnrzk_STECheck.png",
  conversion: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/tl94uv0a_Conversion.png",
};

const RAG_CONFIG = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-800', dot: 'bg-emerald-500', label: 'On Track' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', dot: 'bg-amber-500', label: 'At Risk' },
  red: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-800', dot: 'bg-red-500', label: 'Action Required' },
  grey: { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-600', dot: 'bg-slate-400', label: 'No Data' },
};

const CHART_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

/* ─── RAG Banner ─── */
const RagBanner = ({ status, details }) => {
  const cfg = RAG_CONFIG[status] || RAG_CONFIG.grey;
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border-2 ${cfg.bg} ${cfg.border}`} data-testid="rag-banner">
      <div className={`w-12 h-12 rounded-full ${cfg.dot} flex items-center justify-center shrink-0 shadow-md`}>
        {status === 'green' && <CheckCircle className="w-6 h-6 text-white" />}
        {status === 'amber' && <AlertTriangle className="w-6 h-6 text-white" />}
        {status === 'red' && <XCircle className="w-6 h-6 text-white" />}
        {status === 'grey' && <Gauge className="w-6 h-6 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-lg font-semibold ${cfg.text}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
          Project Status: {cfg.label}
        </p>
        {details?.length > 0 ? (
          <ul className="mt-1 space-y-0.5">
            {details.map((d, i) => (
              <li key={i} className={`text-sm ${cfg.text} opacity-80`}>{d}</li>
            ))}
          </ul>
        ) : status === 'green' ? (
          <p className={`text-sm ${cfg.text} opacity-80`}>All quality indicators are within target thresholds</p>
        ) : status === 'grey' ? (
          <p className={`text-sm ${cfg.text} opacity-80`}>Run BREX validations and delivery comparisons to generate project metrics</p>
        ) : null}
      </div>
    </div>
  );
};

/* ─── KPI Tile ─── */
const KpiTile = ({ label, value, subtitle, color, icon: Icon, testId }) => {
  const colorMap = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    default: 'bg-white border-slate-200 text-slate-900',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
  };
  const cls = colorMap[color] || colorMap.default;
  return (
    <div className={`p-4 rounded-xl border space-y-1 ${cls}`} data-testid={testId}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
        <p className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</p>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs opacity-60">{subtitle}</p>
    </div>
  );
};

/* ─── Conformance Trend Chart ─── */
const ConformanceTrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.map((d, i) => ({
    name: d.date ? new Date(d.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : `Run ${i + 1}`,
    'Schema Pass %': d.schema_pct,
    'BREX Compliance %': d.brex_pct,
    'Total DMs': d.total_dm,
  }));
  return (
    <Card className="border-slate-200" data-testid="conformance-trend-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <TrendingUp className="w-4 h-4 text-teal-600" /> Conformance Trend
        </CardTitle>
        <CardDescription>Schema Pass % and BREX Compliance % over validation runs</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="gradSchema" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradBrex" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
            <Tooltip formatter={(v) => v !== null ? `${v}%` : 'N/A'} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
            <Legend wrapperStyle={{ fontSize: '13px' }} />
            <ReferenceLine y={98} stroke="#0d9488" strokeDasharray="4 4" label={{ value: 'Schema Target 98%', fill: '#0d9488', fontSize: 11, position: 'insideTopRight' }} />
            <ReferenceLine y={95} stroke="#6366f1" strokeDasharray="4 4" label={{ value: 'BREX Target 95%', fill: '#6366f1', fontSize: 11, position: 'insideBottomRight' }} />
            <Area type="monotone" dataKey="Schema Pass %" stroke="#0d9488" fill="url(#gradSchema)" strokeWidth={2} dot={{ r: 4 }} />
            <Area type="monotone" dataKey="BREX Compliance %" stroke="#6366f1" fill="url(#gradBrex)" strokeWidth={2} dot={{ r: 4 }} />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ─── Delivery Trend Chart ─── */
const DeliveryTrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.map((d, i) => ({
    name: d.date ? new Date(d.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : `Run ${i + 1}`,
    'Match %': d.match_pct,
    'DDN Only': d.ddn_only,
    'DML Only': d.dml_only,
    'Matches': d.matches,
  }));
  return (
    <Card className="border-slate-200" data-testid="delivery-trend-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Truck className="w-4 h-4 text-teal-600" /> Delivery Trend
        </CardTitle>
        <CardDescription>DDN/DML match rate over comparison runs</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis yAxisId="left" domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748b' }} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
            <Legend wrapperStyle={{ fontSize: '13px' }} />
            <Bar yAxisId="right" dataKey="Matches" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="DDN Only" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="DML Only" fill="#ef4444" radius={[4, 4, 0, 0]} />
            <Line yAxisId="left" type="monotone" dataKey="Match %" stroke="#0d9488" strokeWidth={2} dot={{ r: 4 }} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ─── Schema Distribution Chart ─── */
const SchemaDistributionChart = ({ data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  const chartData = Object.entries(data).map(([name, value]) => ({ name, value }));
  return (
    <Card className="border-slate-200" data-testid="schema-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Layers className="w-4 h-4 text-teal-600" /> Schema Distribution
        </CardTitle>
        <CardDescription>Data module types in project DML</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center justify-center">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ─── Top Failing BREX Rules Table ─── */
const TopBrexRulesTable = ({ rules }) => {
  if (!rules || rules.length === 0) return null;
  return (
    <Card className="border-slate-200" data-testid="top-brex-rules">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <ShieldCheck className="w-4 h-4 text-red-500" /> Top Failing BREX Rules
        </CardTitle>
        <CardDescription>Most frequently violated business rules across all validation runs</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="brex-rules-table">
            <thead>
              <tr className="border-b bg-slate-50/80">
                <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider w-8">#</th>
                <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rule ID</th>
                <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                <th className="text-right p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Violations</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={r.rule_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="p-2.5 text-sm text-slate-400 font-medium">{i + 1}</td>
                  <td className="p-2.5 text-sm font-mono text-slate-800 whitespace-nowrap">{r.rule_id}</td>
                  <td className="p-2.5 text-sm text-slate-600 max-w-md truncate" title={r.title}>{r.title}</td>
                  <td className="p-2.5 text-right">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      r.count >= 50 ? 'bg-red-100 text-red-700' : r.count >= 20 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                    }`}>{r.count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── Top Schema Errors Table ─── */
const TopSchemaErrorsTable = ({ errors }) => {
  if (!errors || errors.length === 0) return null;
  return (
    <Card className="border-slate-200" data-testid="top-schema-errors">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Top Schema Errors
        </CardTitle>
        <CardDescription>Most common XSD validation errors</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50/80">
                <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider w-8">#</th>
                <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Error</th>
                <th className="text-right p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Count</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((e, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="p-2.5 text-sm text-slate-400 font-medium">{i + 1}</td>
                  <td className="p-2.5 text-sm font-mono text-slate-700 max-w-lg truncate" title={e.error}>{e.error}</td>
                  <td className="p-2.5 text-right">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">{e.count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── BREX Violations Bar Chart (Top 10) ─── */
const BrexViolationsBarChart = ({ rules }) => {
  if (!rules || rules.length === 0) return null;
  const chartData = rules.slice(0, 10).map(r => ({
    rule: r.rule_id.length > 20 ? r.rule_id.substring(0, 20) + '...' : r.rule_id,
    fullRule: r.rule_id,
    title: r.title,
    violations: r.count,
  })).reverse();
  return (
    <Card className="border-slate-200" data-testid="brex-violations-bar-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <ShieldCheck className="w-4 h-4 text-red-500" /> BREX Violations by Rule (Top 10)
        </CardTitle>
        <CardDescription>Most frequently violated business rules</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis type="category" dataKey="rule" tick={{ fontSize: 11, fill: '#64748b' }} width={160} />
            <Tooltip
              formatter={(v, name, props) => [v, 'Violations']}
              labelFormatter={(label, payload) => payload?.[0]?.payload?.fullRule || label}
              contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
            />
            <Bar dataKey="violations" fill="#ef4444" radius={[0, 4, 4, 0]} maxBarSize={28}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.violations >= 50 ? '#dc2626' : entry.violations >= 20 ? '#f59e0b' : '#6366f1'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ─── Schema Errors Bar Chart ─── */
const SchemaErrorsBarChart = ({ errors }) => {
  if (!errors || errors.length === 0) return null;
  const chartData = errors.slice(0, 10).map(e => {
    const short = e.error.length > 50 ? e.error.substring(0, 50) + '...' : e.error;
    return { error: short, fullError: e.error, count: e.count };
  }).reverse();
  return (
    <Card className="border-slate-200" data-testid="schema-errors-bar-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Schema Errors by Type
        </CardTitle>
        <CardDescription>Most common XSD validation errors</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis type="category" dataKey="error" tick={{ fontSize: 10, fill: '#64748b' }} width={200} />
            <Tooltip
              formatter={(v) => [v, 'Occurrences']}
              labelFormatter={(label, payload) => payload?.[0]?.payload?.fullError || label}
              contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', maxWidth: '400px' }}
            />
            <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ─── Risk Heat Map ─── */
const RISK_CELL = {
  green: 'bg-emerald-500/90 text-white',
  amber: 'bg-amber-500/90 text-white',
  red: 'bg-red-500/90 text-white',
  grey: 'bg-slate-200 text-slate-500',
};
const RISK_PILL = {
  green: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  grey: 'bg-slate-100 text-slate-500',
};
const RISK_LABEL = { green: 'Low Risk', amber: 'Medium Risk', red: 'High Risk', grey: 'No Data' };

/* ─── Publication Builds tracking ─── */
const PublicationBuildsWidget = ({ data }) => {
  if (!data || !data.total_builds) return null;
  const srcLabel = { authored: 'Authored DMs', 'working-dir': 'Working Dir', 'xsl-fo': 'XSL-FO' };
  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };
  const rate = data.success_rate;
  const rateCls = rate === null ? 'bg-slate-100 text-slate-600' : rate >= 90 ? 'bg-emerald-100 text-emerald-700' : rate >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return (
    <Card className="border-slate-200" data-testid="publication-builds-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Printer className="w-4 h-4 text-teal-600" /> Publication Builds
          <span className="ml-auto flex items-center gap-2">
            <span className="text-xs font-normal text-slate-500">{data.success_count}/{data.total_builds} succeeded</span>
            {rate !== null && <Badge className={`text-xs ${rateCls}`} data-testid="publication-success-rate">{rate}% success</Badge>}
          </span>
        </CardTitle>
        <CardDescription>PDF publication build outcomes for this project</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5" data-testid="publication-builds-list">
          {(data.recent || []).map(b => (
            <div key={b.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-100 bg-slate-50/70" data-testid={`publication-build-${b.id}`}>
              {b.status === 'success'
                ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-700 truncate">{b.publication}</p>
                {b.status !== 'success' && b.error && (
                  <p className="text-[11px] text-red-500 truncate" title={b.error}>{b.error}</p>
                )}
              </div>
              <Badge variant="outline" className="text-[10px] text-slate-500 shrink-0">{srcLabel[b.source] || b.source}</Badge>
              <span className="text-[11px] text-slate-500 shrink-0">{b.module_count} DM{b.module_count === 1 ? '' : 's'}</span>
              <span className="text-[11px] text-slate-400 shrink-0 hidden md:inline">{b.user_email}</span>
              <span className="text-[11px] text-slate-400 shrink-0">{fmtTime(b.timestamp)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── Forecast (projected completion + workload) ─── */
const ForecastWidget = ({ data }) => {
  if (!data || data.status === 'no_data') return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';
  const statusCfg = {
    on_track: { label: 'On Track', cls: 'bg-emerald-100 text-emerald-700' },
    at_risk: { label: 'At Risk', cls: 'bg-amber-100 text-amber-700' },
    off_track: { label: 'Off Track', cls: 'bg-red-100 text-red-700' },
    complete: { label: 'DMRL Complete', cls: 'bg-emerald-100 text-emerald-700' },
    no_target: { label: 'No Milestone Set', cls: 'bg-slate-100 text-slate-600' },
  }[data.status] || { label: '—', cls: 'bg-slate-100 text-slate-600' };
  const chartData = (data.weekly_trend || []).map(w => ({
    name: new Date(w.week).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }),
    Authored: w.count,
  }));
  const tiles = [
    { label: 'Projected Completion', value: fmt(data.projected_completion), sub: data.projected_weeks !== null && data.projected_weeks !== undefined ? `${data.projected_weeks} wk of work remaining` : 'Author modules to project', testId: 'forecast-projected' },
    { label: 'Authoring Velocity', value: data.velocity_per_week !== null ? `${data.velocity_per_week}/wk` : '--', sub: 'DMs authored (recent pace)', testId: 'forecast-velocity' },
    { label: 'Required Pace', value: data.required_pace_per_week !== null ? `${data.required_pace_per_week}/wk` : '--', sub: data.target_date ? `to hit ${data.target_label || 'milestone'} · ${fmt(data.target_date)}` : 'No delivery milestone set', testId: 'forecast-required-pace' },
    { label: 'Remaining DMs', value: data.remaining_dms !== null ? data.remaining_dms : '--', sub: `of ${data.dm_total || 0} in DMRL`, testId: 'forecast-remaining' },
  ];
  const behind = data.required_pace_per_week !== null && data.velocity_per_week !== null && data.velocity_per_week < data.required_pace_per_week;
  return (
    <Card className="border-slate-200" data-testid="forecast-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <TrendingUp className="w-4 h-4 text-indigo-600" /> Forecast
          <Badge className={`ml-auto text-xs ${statusCfg.cls}`} data-testid="forecast-status">{statusCfg.label}</Badge>
        </CardTitle>
        <CardDescription>Projected completion and workload from DMRL authoring velocity{behind ? ' — current pace is below the required pace' : ''}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {tiles.map(t => (
            <div key={t.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={t.testId}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</p>
              <p className="text-lg font-bold text-slate-800 leading-tight">{t.value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{t.sub}</p>
            </div>
          ))}
        </div>
        {chartData.length > 0 && (
          <div data-testid="forecast-weekly-chart">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Weekly authoring output (last 8 weeks)</p>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="Authored" fill="#6366f1" radius={[3, 3, 0, 0]} />
                {data.required_pace_per_week !== null && (
                  <ReferenceLine y={data.required_pace_per_week} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `required ${data.required_pace_per_week}/wk`, fontSize: 10, fill: '#b45309', position: 'insideTopRight' }} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RiskHeatMap = ({ data }) => (
  <Card className="border-slate-200" data-testid="risk-heat-map">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <Grid3X3 className="w-4 h-4 text-rose-600" /> Risk Heat Map
      </CardTitle>
      <CardDescription>Project risk across schedule, quality and coverage — red cells need attention</CardDescription>
    </CardHeader>
    <CardContent className="space-y-3">
      {data.map((dim) => (
        <div key={dim.dimension} className="flex items-stretch gap-3" data-testid={`risk-row-${dim.dimension.toLowerCase()}`}>
          <div className="w-32 shrink-0 flex flex-col justify-center">
            <p className="text-sm font-semibold text-slate-700">{dim.dimension}</p>
            <Badge className={`text-[10px] w-fit mt-1 ${RISK_PILL[dim.status]}`}>{RISK_LABEL[dim.status]}</Badge>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {dim.cells.map((cell) => (
              <div key={cell.label} className={`rounded-lg px-3 py-2.5 ${RISK_CELL[cell.status]}`} data-testid={`risk-cell-${dim.dimension.toLowerCase()}-${cell.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-[10px] uppercase tracking-wider opacity-80">{cell.label}</p>
                <p className="text-lg font-bold leading-tight">{cell.value}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-4 pt-1 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> On track</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Watch</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> At risk</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" /> No data</span>
      </div>
    </CardContent>
  </Card>
);

/* ─── DMRL Authoring Coverage ─── */
const DmrlAuthoringCoverage = ({ data }) => {
  const total = data.total_entries || 0;
  const authored = data.authored || 0;
  const inDir = data.in_directory || 0;
  const covered = authored + inDir;
  const pct = total > 0 ? Math.round((covered / total) * 1000) / 10 : 0;
  const authoredW = total > 0 ? (authored / total) * 100 : 0;
  const dirW = total > 0 ? (inDir / total) * 100 : 0;
  return (
    <Card className="border-slate-200" data-testid="dmrl-authoring-coverage">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Pencil className="w-4 h-4 text-violet-600" /> DMRL Authoring Coverage
        </CardTitle>
        <CardDescription>Entries with content — authored in the Author tool or existing in the working directory</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <span className="text-3xl font-bold text-slate-800" data-testid="dmrl-coverage-pct">{pct}%</span>
            <span className="text-sm text-slate-500 ml-2">
              {covered} of {total} entries have content
              {inDir > 0 && <span className="text-slate-400"> ({authored} authored · {inDir} existing in directory)</span>}
            </span>
          </div>
          {data.extra_authored > 0 && (
            <Badge variant="outline" className="text-xs text-blue-600 border-blue-200" data-testid="dmrl-extra-authored">
              +{data.extra_authored} authored outside DMRL
            </Badge>
          )}
        </div>
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
          <div className="h-full bg-violet-500" style={{ width: `${Math.min(authoredW, 100)}%`, transition: 'width 0.5s ease' }} />
          <div className="h-full bg-sky-400" style={{ width: `${Math.min(dirW, 100 - authoredW)}%`, transition: 'width 0.5s ease' }} />
        </div>
        <div className="flex items-center gap-4 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> Authored in tool</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-400" /> Existing content in directory</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" /> No content</span>
        </div>
        {data.by_schema?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 pt-1">
            {data.by_schema.map(s => {
              const sAuth = s.total > 0 ? (s.authored / s.total) * 100 : 0;
              const sDir = s.total > 0 ? ((s.in_directory || 0) / s.total) * 100 : 0;
              return (
                <div key={s.schema} className="flex items-center gap-3" data-testid={`dmrl-schema-${s.schema}`}>
                  <span className="text-xs text-slate-600 w-36 truncate" title={s.schema}>{s.schema}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
                    <div className="h-full bg-violet-500" style={{ width: `${sAuth}%` }} />
                    <div className="h-full bg-sky-400" style={{ width: `${Math.min(sDir, 100 - sAuth)}%` }} />
                  </div>
                  <span className="text-xs text-slate-500 w-14 text-right">{s.authored + (s.in_directory || 0)}/{s.total}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── DMRL Coverage by SNS Chart ─── */
const DmrlCoverageBySnsChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <Card className="border-slate-200" data-testid="dmrl-coverage-sns-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Layers className="w-4 h-4 text-teal-600" /> DMRL Coverage by SNS
        </CardTitle>
        <CardDescription>Data module coverage per system (SNS) from delivery DML — matched vs unmatched in DDN</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="sns" tick={{ fontSize: 12, fill: '#64748b' }} label={{ value: 'System Code (SNS)', position: 'insideBottom', offset: -2, style: { fontSize: 11, fill: '#94a3b8' } }} />
            <YAxis tick={{ fontSize: 12, fill: '#64748b' }} label={{ value: 'Data Modules', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#94a3b8' } }} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} formatter={(v, name) => [v, name === 'matched' ? 'Matched in DDN' : 'DML Only']} />
            <Legend wrapperStyle={{ fontSize: '13px' }} formatter={(v) => v === 'matched' ? 'Matched in DDN' : 'DML Only (Not in DDN)'} />
            <Bar dataKey="matched" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
            <Bar dataKey="unmatched" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ─── Project Completion Widget ─── */
const ProjectCompletionWidget = ({ project, dmlEntries, deliveryHistory }) => {
  // Calculate completion: delivered (matched) DMs vs total DML entries
  const totalDms = dmlEntries.length || project?.analytics?.target_total_dms || 0;

  // Count unique delivered/matched DMs from latest delivery run
  let deliveredDms = 0;
  if (deliveryHistory.length > 0) {
    const latestRun = deliveryHistory[0];
    deliveredDms = latestRun.matches_count || 0;
  }

  const completionPct = totalDms > 0 ? Math.min(Math.round((deliveredDms / totalDms) * 100), 100) : 0;
  const remainingDms = Math.max(totalDms - deliveredDms, 0);

  // Roles & rates from the project
  const rolesRates = project?.roles_rates || [];
  const totalRoleCost = rolesRates.reduce((sum, r) => sum + (r.cost || 0), 0);

  // Cost breakdown: spent vs remaining (proportional to completion)
  const spentCost = totalRoleCost * (completionPct / 100);
  const remainingCost = totalRoleCost - spentCost;

  // Currency symbol
  const curr = project?.analytics?.currency || 'USD';
  const sym = curr === 'AUD' ? 'A$' : curr === 'GBP' ? '£' : curr === 'EUR' ? '€' : '$';
  const fmt = (v) => `${sym}${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  // Gauge SVG parameters
  const size = 160;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (completionPct / 100) * circ;
  const gaugeColor = completionPct >= 75 ? '#10b981' : completionPct >= 40 ? '#f59e0b' : '#ef4444';

  // Pie chart data for role cost distribution
  const rolePieData = rolesRates.filter(r => r.cost > 0).map(r => ({
    name: r.name,
    value: r.cost,
  }));

  return (
    <Card className="border-slate-200" data-testid="project-completion-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Gauge className="w-5 h-5 text-teal-600" />
          Project Completion
        </CardTitle>
        <CardDescription>Delivery against DML — {deliveredDms} of {totalDms} data modules delivered</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Completion Gauge */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative" style={{ width: size, height: size }}>
              <svg width={size} height={size} className="-rotate-90">
                {/* Background track */}
                <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
                {/* Progress arc */}
                <circle
                  cx={size/2} cy={size/2} r={radius}
                  fill="none" stroke={gaugeColor} strokeWidth={stroke}
                  strokeLinecap="round"
                  strokeDasharray={circ} strokeDashoffset={offset}
                  className="transition-all duration-700 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-black" style={{ color: gaugeColor }}>{completionPct}%</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Complete</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-4 text-center w-full">
              <div>
                <p className="text-lg font-bold text-slate-800">{totalDms}</p>
                <p className="text-[10px] text-slate-500 uppercase font-semibold">Total DMs</p>
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-600">{deliveredDms}</p>
                <p className="text-[10px] text-slate-500 uppercase font-semibold">Delivered</p>
              </div>
              <div>
                <p className="text-lg font-bold text-amber-600">{remainingDms}</p>
                <p className="text-[10px] text-slate-500 uppercase font-semibold">Remaining</p>
              </div>
            </div>
          </div>

          {/* Role Cost Breakdown */}
          <div className="lg:col-span-2">
            {rolesRates.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Cost by Role</h4>
                  <span className="text-sm font-bold text-slate-800">{fmt(totalRoleCost)} total</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Role bars */}
                  <div className="space-y-2.5">
                    {rolesRates.map((role, i) => {
                      const rolePct = totalRoleCost > 0 ? (role.cost / totalRoleCost) * 100 : 0;
                      const roleDeliveredCost = role.cost * (completionPct / 100);
                      return (
                        <div key={i} data-testid={`role-cost-bar-${i}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-semibold text-slate-700">{role.name || 'Unnamed Role'}</span>
                            <span className="font-mono text-slate-500">{fmt(role.cost)}</span>
                          </div>
                          <div className="h-5 bg-slate-100 rounded-sm overflow-hidden relative">
                            <div
                              className="h-full rounded-sm transition-all duration-500"
                              style={{ width: `${rolePct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length], opacity: 0.2 }}
                            />
                            <div
                              className="absolute top-0 left-0 h-full rounded-sm transition-all duration-500"
                              style={{ width: `${rolePct * (completionPct / 100)}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-600">
                              {fmt(roleDeliveredCost)} delivered
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Mini pie chart */}
                  {rolePieData.length > 0 && (
                    <div className="flex items-center justify-center">
                      <ResponsiveContainer width={200} height={200}>
                        <PieChart>
                          <Pie
                            data={rolePieData}
                            cx="50%" cy="50%"
                            outerRadius={80} innerRadius={45}
                            dataKey="value"
                            stroke="none"
                          >
                            {rolePieData.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                {/* Cost summary row */}
                <div className="grid grid-cols-3 gap-3 mt-2 pt-3 border-t border-slate-200">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-slate-800">{fmt(totalRoleCost)}</p>
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Cost</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-emerald-600">{fmt(spentCost)}</p>
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Spent (est.)</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-amber-600">{fmt(remainingCost)}</p>
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Remaining (est.)</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-center p-8 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">No roles & rates configured.</p>
                  <p className="text-xs text-slate-400 mt-1">Edit this project's Roles & Rates tab to see cost breakdown.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ═════════════════ MAIN PAGE ═════════════════ */
const ProjectDashboardPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState({ delivery_count: 0, dml_count: 0, brex_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [brexHistory, setBrexHistory] = useState([]);
  const [brexExpanded, setBrexExpanded] = useState(false);
  const [expandedValidation, setExpandedValidation] = useState(null);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [deliveryExpanded, setDeliveryExpanded] = useState(false);
  const [expandedDelivery, setExpandedDelivery] = useState(null);
  const [dmlEntries, setDmlEntries] = useState([]);
  const [dmlExpanded, setDmlExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState('default');
  const [brexBuilderDocs, setBrexBuilderDocs] = useState([]);
  const [brexBuilderExpanded, setBrexBuilderExpanded] = useState(false);
  const [brexMatrixExpanded, setBrexMatrixExpanded] = useState(false);
  const [brexAnalyticsExpanded, setBrexAnalyticsExpanded] = useState(false);
  const [brexModulesExpanded, setBrexModulesExpanded] = useState(false);
  const [brexModulesEditing, setBrexModulesEditing] = useState(false);
  const [brexModulesForm, setBrexModulesForm] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [allowedTools, setAllowedTools] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/product-configs/my-tools`, { headers })
      .then(res => setAllowedTools(res.data.tools || []))
      .catch(() => setAllowedTools(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/projects/${projectId}/summary`, { headers });
      setProject(res.data.project);
      setSummary({ delivery_count: res.data.delivery_count, dml_count: res.data.dml_count, brex_count: res.data.brex_count });
    } catch {
      toast.error('Failed to load project');
      navigate('/dashboard/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  const fetchBrexHistory = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/brex/history?project_id=${projectId}`, { headers }); setBrexHistory(res.data); } catch {}
  }, [projectId]);

  const fetchDeliveryHistory = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/delivery/history?project_id=${projectId}`, { headers }); setDeliveryHistory(res.data); } catch {}
  }, [projectId]);

  const fetchDmlEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/dml?project_id=${projectId}`, { headers });
      if (res.data.length > 0) {
        const detail = await axios.get(`${API}/api/dml/${res.data[0].id}`, { headers });
        setDmlEntries(detail.data.entries || []);
      }
    } catch {}
  }, [projectId]);

  const fetchMetrics = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/projects/${projectId}/metrics`, { headers }); setMetrics(res.data); } catch {}
  }, [projectId]);

  // ── DMRL coverage enrichment: working directory files + authored modules ──
  const [dirXmlNames, setDirXmlNames] = useState(null);
  const [authoredDmcs, setAuthoredDmcs] = useState(null);

  useEffect(() => {
    (async () => {
      setDirXmlNames(null);
      try {
        const rec = await getDirectoryHandle(projectId);
        const handle = rec?.handle;
        if (!handle) return;
        const perm = await handle.queryPermission({ mode: 'read' });
        if (perm !== 'granted') return;
        const files = await listFiles(handle);
        setDirXmlNames(files.filter(f => f.kind === 'file' && /\.xml$/i.test(f.name)).map(f => f.name.toUpperCase()));
      } catch { /* no directory linked */ }
    })();
  }, [projectId]);

  useEffect(() => {
    (async () => {
      setAuthoredDmcs(null);
      try {
        const res = await axios.get(`${API}/api/author/modules/${projectId}`, { headers });
        setAuthoredDmcs((res.data || []).map(m => (m.dmc || '').toUpperCase()).filter(Boolean));
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const dmrlCoverageData = useMemo(() => {
    const base = metrics?.dmrl_coverage;
    if (!base) return null;
    if (!dirXmlNames?.length || !dmlEntries.length) return base;
    const authoredSet = new Set(authoredDmcs || []);
    let authored = 0, inDir = 0;
    const bySchema = {};
    for (const e of dmlEntries) {
      const s = e.schema || 'Unspecified';
      if (!bySchema[s]) bySchema[s] = { schema: s, total: 0, authored: 0, in_directory: 0 };
      bySchema[s].total += 1;
      const dmcU = (e.dmc_formatted || '').toUpperCase();
      const pmU = (e.pm_code || '').toUpperCase();
      const ddnU = (e.ddn_code || '').toUpperCase();
      if (dmcU && authoredSet.has(dmcU)) { authored++; bySchema[s].authored++; continue; }
      const hasFile = dirXmlNames.some(n => (dmcU && n.startsWith(dmcU)) || (pmU && n.startsWith(pmU)) || (ddnU && n.startsWith(ddnU)));
      if (hasFile) { inDir++; bySchema[s].in_directory++; }
    }
    const total = dmlEntries.length;
    return {
      total_entries: total,
      authored,
      in_directory: inDir,
      coverage_pct: total ? Math.round(((authored + inDir) / total) * 1000) / 10 : null,
      extra_authored: base.extra_authored,
      by_schema: Object.values(bySchema).sort((a, b) => a.schema.localeCompare(b.schema)),
    };
  }, [metrics, dirXmlNames, authoredDmcs, dmlEntries]);

  const riskHeatmapData = useMemo(() => {
    const rh = metrics?.risk_heatmap;
    if (!rh) return null;
    const cov = dmrlCoverageData;
    if (!cov || cov.in_directory === undefined || cov.coverage_pct === null) return rh;
    const order = { red: 0, amber: 1, grey: 2, green: 3 };
    return rh.map(dim => {
      if (dim.dimension !== 'Coverage') return dim;
      const cells = dim.cells.map(c => c.label === 'DMRL Authoring' ? {
        label: c.label,
        value: `${cov.coverage_pct}%`,
        status: cov.coverage_pct >= 80 ? 'green' : cov.coverage_pct >= 50 ? 'amber' : 'red',
      } : c);
      const nonGrey = cells.filter(c => c.status !== 'grey').map(c => c.status);
      const status = nonGrey.length ? nonGrey.reduce((a, b) => (order[a] < order[b] ? a : b)) : 'grey';
      return { ...dim, status, cells };
    });
  }, [metrics, dmrlCoverageData]);

  const fetchBrexBuilderDocs = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/brex-builder/generated-docs/${projectId}`, { headers }); setBrexBuilderDocs(res.data || []); } catch {}
  }, [projectId]);

  useEffect(() => { fetchSummary(); fetchBrexHistory(); fetchDeliveryHistory(); fetchDmlEntries(); fetchMetrics(); fetchBrexBuilderDocs(); }, [fetchSummary, fetchBrexHistory, fetchDeliveryHistory, fetchDmlEntries, fetchMetrics, fetchBrexBuilderDocs]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${project?.title}"? This action cannot be undone.`)) return;
    try { await axios.delete(`${API}/api/projects/${projectId}`, { headers }); toast.success('Project deleted'); navigate('/dashboard/projects'); } catch { toast.error('Failed to delete project'); }
  };

  const handleToolClick = (path) => {
    navigate(path, { state: { project, projectId: project?.id, projectTitle: project?.title } });
  };

  const loadValidationDetail = async (id) => {
    if (expandedValidation?.id === id) { setExpandedValidation(null); return; }
    try { const res = await axios.get(`${API}/api/brex/history/${id}`, { headers }); setExpandedValidation(res.data); } catch { toast.error('Failed to load detail'); }
  };

  const handleDownloadZip = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API}/api/brex/export-all-zip/${id}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'brex_validation_reports.zip';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const loadDeliveryDetail = async (id) => {
    if (expandedDelivery?.id === id) { setExpandedDelivery(null); return; }
    try { const res = await axios.get(`${API}/api/delivery/history/${id}`, { headers }); setExpandedDelivery(res.data); } catch { toast.error('Failed to load detail'); }
  };

  const exportDeliveryReport = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API}/api/delivery/history/${id}`, { headers });
      const d = res.data;
      const esc = (v) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
      const rows = [
        ['DDN/DML Comparison Report'],
        ['Generated', new Date().toISOString()],
        ['Project', d.project_title || 'N/A'],
        [],
        ['Summary'],
        ['DDN Entries', d.ddn_entries?.length || 0],
        ['DML Entries', d.dml_entries?.length || 0],
        ['Matches', d.matches?.length || 0],
        ['DDN Only', d.in_ddn_only?.length || 0],
        ['DML Only', d.in_dml_only?.length || 0],
        ['Match Rate %', d.match_percentage],
        [],
        ['Entry', 'Status'],
      ];
      (d.matches || []).forEach(entry => rows.push([entry, 'Match']));
      (d.in_ddn_only || []).forEach(entry => rows.push([entry, 'DDN Only']));
      (d.in_dml_only || []).forEach(entry => rows.push([entry, 'DML Only']));
      const csv = rows.map(r => r.map(c => esc(c)).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `comparison_report_${new Date(d.created_at).toISOString().split('T')[0]}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const exportProject = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/projects/${projectId}/export`, { headers });
      const fileName = `${(project?.title || 'project').replace(/[^a-zA-Z0-9]/g, '_')}_export_${new Date().toISOString().split('T')[0]}.json`;
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click(); URL.revokeObjectURL(url);
      toast.success('Project exported successfully');
    } catch { toast.error('Failed to export project'); } finally { setExporting(false); }
  };

  const handleBrexModuleUpload = async (field) => {
    try {
      const input = document.createElement('input');
      input.type = 'file'; input.accept = '.xml,.XML';
      input.onchange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const filename = file.name;
        setBrexModulesForm(prev => ({ ...prev, [field]: filename }));
        toast.success(`${field} set to: ${filename}`);
      };
      input.click();
    } catch { toast.error('Failed to select file'); }
  };

  const saveBrexModules = async () => {
    try {
      await axios.put(`${API}/api/projects/${projectId}`, {
        brex_modules: brexModulesForm,
      }, { headers });
      setProject(prev => ({ ...prev, brex_modules: brexModulesForm }));
      setBrexModulesEditing(false);
      toast.success('BREX modules updated');
    } catch { toast.error('Failed to update BREX modules'); }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="project-dashboard-loading">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const kpi = metrics?.kpi || {};
  const hasMetrics = metrics && (kpi.validations_run > 0 || kpi.delivery_comparisons > 0 || kpi.dm_count > 0);

  /* ─── Helpers for KPI color ─── */
  const pctColor = (val, goodThreshold, warnThreshold) => {
    if (val === null || val === undefined) return 'default';
    if (val >= goodThreshold) return 'green';
    if (val >= warnThreshold) return 'amber';
    return 'red';
  };

  const actionCards = [
    { id: 'edit', title: 'Edit Project', desc: 'Modify project settings and metadata', image: IMAGES.editProject, count: null, onClick: () => setShowEditModal(true) },
    { id: 'delivery', title: 'Delivery', desc: 'DDN/DML comparison and file validation', image: IMAGES.delivery, count: summary.delivery_count, countLabel: 'comparisons', onClick: () => handleToolClick('/dashboard/tools/delivery') },
    { id: 'dml-builder', title: 'DML Builder', desc: 'Data Module List management', image: IMAGES.dmlBuilder, count: summary.dml_count, countLabel: 'data modules', onClick: () => handleToolClick('/dashboard/tools/dml-builder') },
    { id: 'brex-validation', title: 'BREX Validation', desc: 'Business rules compliance checks', image: IMAGES.brexValidation, count: summary.brex_count, countLabel: 'validations', onClick: () => handleToolClick('/dashboard/tools/brex-validation') },
    { id: 'brex-builder', title: 'BREX Builder', desc: 'Build BREX decision points and modules', image: IMAGES.brexBuilder, count: null, onClick: () => handleToolClick('/dashboard/tools/brex-builder') },
    { id: 'author', title: 'Author', desc: 'Create and edit data module content', image: IMAGES.author, count: null, onClick: () => handleToolClick('/dashboard/tools/author') },
    { id: 'pdf-publish', title: 'PDF Publish', desc: 'Publish data modules to PDF', image: IMAGES.pdfPublish, count: null, onClick: () => handleToolClick('/dashboard/tools/pdf-publish') },
    { id: 'ste-check', title: 'STE Check', desc: 'Check STE compliance', image: IMAGES.steCheck, count: null, onClick: () => handleToolClick('/dashboard/tools/ste-check') },
    { id: 'conversion', title: 'Conversion', desc: 'Convert PDF/Word documents to S1000D', image: IMAGES.conversion, count: null, onClick: () => handleToolClick('/dashboard/tools/conversion') },
  ].filter((card) => card.id === 'edit' || !allowedTools || allowedTools.includes(card.id));

  return (
    <div className="space-y-6" data-testid="project-dashboard">
      {/* ═══ Header ═══ */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <button onClick={() => navigate('/dashboard/projects')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600 transition-colors mb-2" data-testid="back-to-projects">
            <ArrowLeft className="w-4 h-4" />Back to Projects
          </button>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="project-title">{project?.title}</h1>
          {project?.description && <p className="text-slate-500 max-w-2xl">{project.description}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {project?.s1000d_version && (
              <span className="inline-block px-2.5 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">S1000D {project.s1000d_version}</span>
            )}
            {project?.company && (
              <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">{project.company}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => window.print()} className="text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="print-project-btn">
            <Printer className="w-4 h-4 mr-1.5" />Print
          </Button>
          <Button variant="outline" size="sm" onClick={exportProject} disabled={exporting} className="text-teal-600 border-teal-200 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300" data-testid="export-project-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300" data-testid="delete-project-btn">
            <Trash2 className="w-4 h-4 mr-1.5" />Delete
          </Button>
        </div>
      </div>

      {/* ═══ RAG Status Banner ═══ */}
      {metrics && <RagBanner status={metrics.rag_status} details={metrics.rag_details} />}

      {/* ═══ Tab Navigation ═══ */}
      <div className="flex border-b border-slate-200" data-testid="project-tabs">
        {[
          { id: 'default', label: 'Default', icon: Gauge },
          { id: 'brex', label: 'BREX', icon: ShieldCheck },
          { id: 'dmrl', label: 'DMRL', icon: Layers },
          { id: 'tools', label: 'Tools and Actions', icon: Wrench },
          { id: 'history', label: 'History', icon: Clock },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-teal-600 text-teal-700 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════ DEFAULT TAB ═══════ */}
      {activeTab === 'default' && (
        <div className="space-y-6" data-testid="tab-content-default">
          {hasMetrics ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-tiles">
                <KpiTile label="Schema Pass" value={kpi.schema_pass_pct !== null ? `${kpi.schema_pass_pct}%` : '--'} subtitle={kpi.schema_pass_pct !== null ? 'XSD validation rate' : 'No validations yet'} icon={CheckCircle} color={pctColor(kpi.schema_pass_pct, 98, 88)} testId="kpi-schema-pass" />
                <KpiTile label="BREX Compliance" value={kpi.brex_compliance_pct !== null ? `${kpi.brex_compliance_pct}%` : '--'} subtitle={kpi.brex_compliance_pct !== null ? 'Business rules pass rate' : 'No validations yet'} icon={ShieldCheck} color={pctColor(kpi.brex_compliance_pct, 95, 85)} testId="kpi-brex-compliance" />
                <KpiTile label="STE Compliance" value={kpi.ste_compliance_pct !== null && kpi.ste_compliance_pct !== undefined ? `${kpi.ste_compliance_pct}%` : '--'} subtitle={kpi.ste_compliance_pct !== null && kpi.ste_compliance_pct !== undefined ? 'ASD-STE100 Issue 9' : 'No checks yet'} icon={ShieldCheck} color={pctColor(kpi.ste_compliance_pct, 90, 70)} testId="kpi-ste-compliance" />
                <KpiTile label="Delivery Match" value={kpi.avg_delivery_match_pct !== null ? `${kpi.avg_delivery_match_pct}%` : '--'} subtitle={kpi.avg_delivery_match_pct !== null ? 'Avg DDN/DML match' : 'No comparisons yet'} icon={Truck} color={pctColor(kpi.avg_delivery_match_pct, 95, 80)} testId="kpi-delivery-match" />
                <KpiTile label="Validations Run" value={kpi.validations_run} subtitle="BREX checks performed" icon={Activity} color="teal" testId="kpi-validations" />
                <KpiTile label="Comparisons" value={kpi.delivery_comparisons} subtitle="DDN/DML deliveries" icon={BarChart3} color="teal" testId="kpi-comparisons" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {metrics?.conformance?.brex_trend?.length > 0 && <ConformanceTrendChart data={metrics.conformance.brex_trend} />}
                {metrics?.modularity?.schema_distribution && Object.keys(metrics.modularity.schema_distribution).length > 0 && <SchemaDistributionChart data={metrics.modularity.schema_distribution} />}
              </div>

              {/* ═══ Risk Heat Map ═══ */}
              {riskHeatmapData && <RiskHeatMap data={riskHeatmapData} />}

              {/* ═══ Forecast ═══ */}
              {metrics?.forecast && <ForecastWidget data={metrics.forecast} />}

              {/* ═══ Publication Builds ═══ */}
              {metrics?.publication && <PublicationBuildsWidget data={metrics.publication} />}

              {/* ═══ Project Completion ═══ */}
              <ProjectCompletionWidget project={project} dmlEntries={dmlEntries} deliveryHistory={deliveryHistory} />

              {/* ═══ STE Compliance Summary ═══ */}
              {metrics?.ste ? (
                <Card className="border-slate-200" data-testid="ste-compliance-summary">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <ShieldCheck className="w-5 h-5 text-indigo-600" />
                      STE Compliance
                      <Badge className={`ml-auto text-xs ${
                        metrics.ste.compliance_pct === 100 ? 'bg-emerald-100 text-emerald-700' :
                        metrics.ste.compliance_pct >= 70 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {metrics.ste.compliance_pct}%
                      </Badge>
                    </CardTitle>
                    <CardDescription>ASD-STE100 Issue 9 — last check {metrics.ste.checked_at ? new Date(metrics.ste.checked_at).toLocaleString() : ''}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Summary stats */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-slate-800">{metrics.ste.total_dms}</p>
                        <p className="text-[10px] text-slate-500 uppercase">DMs Checked</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-emerald-600">{metrics.ste.compliant_dms}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Compliant</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-red-600">{metrics.ste.non_compliant_dms}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Non-Compliant</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-amber-600">{metrics.ste.total_violations}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Total Violations</p>
                      </div>
                    </div>

                    {/* Rule summary */}
                    {metrics.ste.rule_summary?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Rule Breakdown</p>
                        <div className="space-y-1">
                          {metrics.ste.rule_summary.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <span className="font-mono text-xs text-slate-400 w-12 shrink-0">{r.rule}</span>
                              <span className="text-slate-600 flex-1 truncate">{r.name}</span>
                              {r.total_issues === 0 ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Pass</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[10px]"><XCircle className="w-2.5 h-2.5 mr-0.5" /> {r.total_issues} in {r.dms_failed} DM{r.dms_failed !== 1 ? 's' : ''}</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Non-compliant DMs list */}
                    {metrics.ste.dm_summaries?.some(d => !d.compliant) && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Non-Compliant DMs</p>
                        <div className="space-y-1 max-h-[160px] overflow-y-auto">
                          {metrics.ste.dm_summaries.filter(d => !d.compliant).map((d, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-red-50/50 border border-red-100">
                              <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                              <span className="font-medium text-slate-700 truncate flex-1">{d.title || d.dmc}</span>
                              <span className="text-slate-400">{d.violations_count} violations</span>
                              <div className="flex gap-0.5">
                                {d.rules_failed?.map((rf, j) => (
                                  <Badge key={j} variant="outline" className="text-[9px] px-1">{rf.rule}</Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-dashed border-2 border-slate-200" data-testid="ste-compliance-summary">
                  <CardContent className="flex items-center gap-4 py-6">
                    <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">STE Compliance</p>
                      <p className="text-sm text-slate-400">Run an STE Check from the tools to see compliance summary here</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="space-y-6">
              <ProjectCompletionWidget project={project} dmlEntries={dmlEntries} deliveryHistory={deliveryHistory} />
              {riskHeatmapData && <RiskHeatMap data={riskHeatmapData} />}
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Gauge className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>No Validation Data Yet</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Run BREX validations and delivery comparisons to generate project metrics</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ BREX TAB ═══════ */}
      {activeTab === 'brex' && (
        <div className="space-y-6" data-testid="tab-content-brex">
          {/* BREX Rule Failures */}
          {metrics?.conformance?.top_failing_brex_rules?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TopBrexRulesTable rules={metrics.conformance.top_failing_brex_rules} />
              <BrexViolationsBarChart rules={metrics.conformance.top_failing_brex_rules} />
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="flex items-center gap-4 py-8">
                <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">No BREX rule failures recorded yet</p>
                  <p className="text-sm text-slate-500">Run a BREX validation to see top failing rules and the violations breakdown.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* BREX Traceability Matrix */}
          <Card className="border-slate-200" data-testid="brex-traceability-matrix">
            <CardHeader className="cursor-pointer select-none" onClick={() => setBrexMatrixExpanded(!brexMatrixExpanded)}>
              <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {brexMatrixExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                <Grid3X3 className="w-5 h-5 text-teal-600" />
                BREX Traceability Matrix
              </CardTitle>
              <CardDescription>DM x BRDP coverage: click cells for detail, refresh to rerun all BREX checks.</CardDescription>
            </CardHeader>
            {brexMatrixExpanded && (
              <CardContent>
                {metrics?.brex_traceability?.rows?.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-slate-500">
                      <span>
                        Latest run: {metrics.brex_traceability.run_timestamp ? new Date(metrics.brex_traceability.run_timestamp).toLocaleString() : '—'}
                        {' · '}{metrics.brex_traceability.total_dms} DMs checked
                        {' · '}{metrics.brex_traceability.total_rules_failing} rule{metrics.brex_traceability.total_rules_failing !== 1 ? 's' : ''} with failures
                        {metrics.brex_traceability.total_rules_failing > metrics.brex_traceability.rules.length && ` (top ${metrics.brex_traceability.rules.length} shown)`}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" /> Pass</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Fail (hover for detail)</span>
                      </span>
                    </div>
                    <div className="overflow-auto max-h-[480px] border border-slate-200 rounded-lg">
                      <table className="w-full text-xs" data-testid="brex-traceability-table">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-slate-100">
                            <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-100 min-w-[220px]">Data Module</th>
                            {metrics.brex_traceability.rules.map((rule) => (
                              <th key={rule.id} className="px-2 py-2 font-mono text-[10px] text-slate-600 whitespace-nowrap" title={rule.title}>
                                {rule.id.replace(/^BRDP-/, '')}
                                <span className="block text-[9px] font-sans font-normal text-red-500">{rule.fail_count} fail{rule.fail_count !== 1 ? 's' : ''}</span>
                              </th>
                            ))}
                            <th className="px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider">Overall</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metrics.brex_traceability.rows.map((row, i) => (
                            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`brex-matrix-row-${i}`}>
                              <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700 sticky left-0 bg-white truncate max-w-[280px]" title={row.filename}>
                                {row.filename || row.dmc}
                              </td>
                              {metrics.brex_traceability.rules.map((rule) => {
                                const failMsg = row.fails?.[rule.id];
                                return (
                                  <td key={rule.id} className="px-2 py-1.5 text-center">
                                    {failMsg ? (
                                      <span className="inline-block w-4 h-4 rounded-sm bg-red-500 cursor-help" title={`${rule.id}: ${failMsg}`} />
                                    ) : (
                                      <span className="inline-block w-4 h-4 rounded-sm bg-emerald-400/70" title={`${rule.id}: Pass`} />
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-3 py-1.5 text-center">
                                {row.passed ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Pass</Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 text-[10px]">{Object.keys(row.fails || {}).length} fail{Object.keys(row.fails || {}).length !== 1 ? 's' : ''}</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Grid3X3 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Traceability matrix will populate after running BREX validations with linked BRDP data.</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* BREX Modules */}
          <Card className="border-slate-200" data-testid="brex-modules-section">
            <CardHeader className="cursor-pointer select-none" onClick={() => {
              setBrexModulesExpanded(!brexModulesExpanded);
              if (!brexModulesExpanded && !brexModulesEditing) {
                setBrexModulesForm(project?.brex_modules || {});
              }
            }}>
              <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {brexModulesExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                <Layers className="w-5 h-5 text-violet-600" />
                BREX Modules
              </CardTitle>
              <CardDescription>Manage the BREX module assignments for this project. These are used in DDN generation and compliance tracking.</CardDescription>
            </CardHeader>
            {brexModulesExpanded && (
              <CardContent className="space-y-4">
                {(() => {
                  const modules = brexModulesEditing ? brexModulesForm : (project?.brex_modules || {});
                  const fields = [
                    { key: 'project', label: 'Project', desc: 'Project-specific BREX module DMC (auto-populated by BREX Builder)' },
                    { key: 's1000d_default', label: 'S1000D Default', desc: 'Default S1000D BREX reference' },
                    { key: 'department', label: 'Department', desc: 'Department-level BREX module' },
                    { key: 'division', label: 'Division', desc: 'Division-level BREX module' },
                    { key: 'organisation', label: 'Organisation', desc: 'Organisation-level BREX module' },
                  ];
                  return (
                    <>
                      <div className="space-y-3">
                        {fields.map(f => (
                          <div key={f.key} className="flex items-start gap-3">
                            <div className="w-32 flex-shrink-0 pt-1.5">
                              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">{f.label}</p>
                              <p className="text-[10px] text-slate-400">{f.desc}</p>
                            </div>
                            <div className="flex-1 flex items-center gap-2">
                              {brexModulesEditing ? (
                                <>
                                  <Input
                                    value={modules[f.key] || ''}
                                    onChange={e => setBrexModulesForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    placeholder={`Enter ${f.label} BREX DMC...`}
                                    className="h-8 text-xs font-mono"
                                    data-testid={`brex-module-${f.key}-input`}
                                  />
                                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 flex-shrink-0"
                                    onClick={(e) => { e.stopPropagation(); handleBrexModuleUpload(f.key); }}
                                    title="Upload XML file"
                                    data-testid={`brex-module-${f.key}-upload`}
                                  >
                                    <Upload className="w-3.5 h-3.5 text-violet-600" />
                                  </Button>
                                </>
                              ) : (
                                <code className={`text-xs font-mono px-2 py-1 rounded ${modules[f.key] ? 'bg-violet-50 text-violet-700' : 'bg-slate-50 text-slate-400 italic'}`}
                                  data-testid={`brex-module-${f.key}-value`}
                                >
                                  {modules[f.key] || 'Not set'}
                                </code>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-2 border-t">
                        {brexModulesEditing ? (
                          <>
                            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5"
                              onClick={(e) => { e.stopPropagation(); saveBrexModules(); }}
                              data-testid="brex-modules-save-btn"
                            >
                              <Save className="w-3.5 h-3.5" /> Save Changes
                            </Button>
                            <Button variant="outline" size="sm"
                              onClick={(e) => { e.stopPropagation(); setBrexModulesEditing(false); setBrexModulesForm(project?.brex_modules || {}); }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button variant="outline" size="sm" className="gap-1.5"
                            onClick={(e) => { e.stopPropagation(); setBrexModulesEditing(true); setBrexModulesForm(project?.brex_modules || {}); }}
                            data-testid="brex-modules-edit-btn"
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit BREX Modules
                          </Button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            )}
          </Card>

          {/* BREX Analytics */}
          <Card className="border-slate-200" data-testid="brex-analytics-section">
            <CardHeader className="cursor-pointer select-none" onClick={() => setBrexAnalyticsExpanded(!brexAnalyticsExpanded)}>
              <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {brexAnalyticsExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                <BarChart3 className="w-5 h-5 text-teal-600" />
                BREX Analytics
              </CardTitle>
              <CardDescription>Completion metrics, coverage, rule distribution, and impact analysis.</CardDescription>
            </CardHeader>
            {brexAnalyticsExpanded && (
              <CardContent className="space-y-6">
                {metrics?.conformance?.top_schema_errors?.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <TopSchemaErrorsTable errors={metrics.conformance.top_schema_errors} />
                    <SchemaErrorsBarChart errors={metrics.conformance.top_schema_errors} />
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <BarChart3 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Analytics will populate after running BREX validations.</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* BREX Validation History */}
          {brexHistory.length > 0 && (
            <Card className="border-slate-200" data-testid="project-brex-history">
              <CardHeader className="cursor-pointer select-none" onClick={() => setBrexExpanded(!brexExpanded)}>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {brexExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  <ShieldCheck className="w-5 h-5 text-teal-600" />
                  BREX Validation History
                  <Badge variant="secondary" className="ml-2">{brexHistory.length}</Badge>
                </CardTitle>
                {!brexExpanded && <CardDescription>Click to view validation history for this project</CardDescription>}
              </CardHeader>
              {brexExpanded && (
                <CardContent className="space-y-2">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="project-brex-table">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date/Time</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">DMs</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">BREX Pass</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">BREX Fail</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Schema Pass</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Schema Fail</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Result</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brexHistory.map((item) => {
                          const allPassed = item.brex_failed === 0 && item.schema_failed === 0;
                          return (
                            <React.Fragment key={item.id}>
                              <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => loadValidationDetail(item.id)} data-testid={`brex-history-row-${item.id}`}>
                                <td className="p-3 text-sm text-slate-700">{new Date(item.timestamp).toLocaleString()}</td>
                                <td className="p-3 text-sm text-slate-700 font-medium">{item.total_dm}</td>
                                <td className="p-3 text-sm text-emerald-700 font-medium">{item.brex_passed}</td>
                                <td className="p-3 text-sm text-red-700 font-medium">{item.brex_failed}</td>
                                <td className="p-3 text-sm text-emerald-700 font-medium">{item.schema_passed}</td>
                                <td className="p-3 text-sm text-red-700 font-medium">{item.schema_failed}</td>
                                <td className="p-3 text-sm">
                                  {allPassed ? (
                                    <span className="flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle className="w-4 h-4" />PASS</span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-red-700 font-medium"><XCircle className="w-4 h-4" />FAIL</span>
                                  )}
                                </td>
                                <td className="p-3 text-sm">
                                  <Button variant="outline" size="sm" onClick={(e) => handleDownloadZip(item.id, e)} data-testid={`brex-download-${item.id}`}>
                                    <Download className="w-3 h-3 mr-1" />Reports
                                  </Button>
                                </td>
                              </tr>
                              {expandedValidation?.id === item.id && (
                                <tr>
                                  <td colSpan={8} className="p-4 bg-slate-50">
                                    <div className="space-y-4">
                                      {expandedValidation.brex_results?.some(r => !r.passed) && (
                                        <div>
                                          <p className="text-sm font-semibold text-red-700 mb-2">BREX Failures</p>
                                          <div className="space-y-2 max-h-64 overflow-y-auto">
                                            {expandedValidation.brex_results.filter(r => !r.passed).map((r, i) => (
                                              <div key={i} className="p-2 bg-red-50 rounded border border-red-100">
                                                <p className="text-sm font-medium font-mono text-slate-900">{r.dmc}</p>
                                                {r.failures.map((f, j) => (
                                                  <div key={j} className="ml-4 mt-1 text-sm text-slate-600 border-l-2 border-red-200 pl-3">
                                                    <span className="font-semibold text-red-600">{f.brdp_id}</span>
                                                    {f.brdp_title && <span className="ml-1">- {f.brdp_title}</span>}
                                                    {f.location && <p className="text-xs text-slate-500 font-mono">Location: {f.location}</p>}
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {expandedValidation.schema_results?.some(r => !r.passed) && (
                                        <div>
                                          <p className="text-sm font-semibold text-amber-700 mb-2">Schema Failures</p>
                                          <div className="space-y-2 max-h-64 overflow-y-auto">
                                            {expandedValidation.schema_results.filter(r => !r.passed).map((r, i) => (
                                              <div key={i} className="p-2 bg-amber-50 rounded border border-amber-100">
                                                <p className="text-sm font-medium font-mono text-slate-900">{r.dmc}</p>
                                                {r.errors.map((err, j) => <p key={j} className="ml-4 text-sm text-amber-700">{err}</p>)}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {expandedValidation.brex_results?.every(r => r.passed) && expandedValidation.schema_results?.every(r => r.passed) && (
                                        <p className="text-emerald-700 text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" />All data modules passed BREX and Schema validation</p>
                                      )}
                                      {expandedValidation.brex_results?.some(r => r.passed) && (
                                        <div>
                                          <p className="text-sm font-semibold text-emerald-700 mb-1">Passed ({expandedValidation.brex_results.filter(r => r.passed).length})</p>
                                          <div className="flex flex-wrap gap-1">
                                            {expandedValidation.brex_results.filter(r => r.passed).map((r, i) => (
                                              <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-mono rounded">{r.dmc}</span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ═══════ DMRL TAB ═══════ */}
      {activeTab === 'dmrl' && (
        <div className="space-y-6" data-testid="tab-content-dmrl">
          {dmrlCoverageData?.total_entries > 0 && <DmrlAuthoringCoverage data={dmrlCoverageData} />}
          {metrics?.delivery?.sns_distribution?.length > 0 ? (
            <>
              <DmrlCoverageBySnsChart data={metrics.delivery.sns_distribution} />
              {metrics?.delivery?.trend?.length > 0 && <DeliveryTrendChart data={metrics.delivery.trend} />}
            </>
          ) : !metrics?.dmrl_coverage?.total_entries ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Layers className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>No DMRL Data</p>
              <p className="text-sm text-slate-500 mt-1">Add DML entries or run delivery comparisons to see DMRL coverage and trends.</p>
            </div>
          ) : null}

          {/* Delivery History */}
          {deliveryHistory.length > 0 && (
            <Card className="border-slate-200" data-testid="project-delivery-history">
              <CardHeader className="cursor-pointer select-none" onClick={() => setDeliveryExpanded(!deliveryExpanded)}>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {deliveryExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  <Truck className="w-5 h-5 text-teal-600" />
                  Delivery Comparisons
                  <Badge variant="secondary" className="ml-2">{deliveryHistory.length}</Badge>
                </CardTitle>
                {!deliveryExpanded && <CardDescription>Click to view DDN/DML comparison history</CardDescription>}
              </CardHeader>
              {deliveryExpanded && (
                <CardContent className="space-y-2">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="project-delivery-table">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date/Time</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">DDN Files</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">DML Files</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Matches</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">DDN Only</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">DML Only</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Match %</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveryHistory.map((item) => (
                          <React.Fragment key={item.id}>
                            <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => loadDeliveryDetail(item.id)} data-testid={`delivery-history-row-${item.id}`}>
                              <td className="p-3 text-sm text-slate-700">{new Date(item.created_at).toLocaleString()}</td>
                              <td className="p-3 text-sm text-slate-700 font-medium">{item.ddn_file_count}</td>
                              <td className="p-3 text-sm text-slate-700 font-medium">{item.dml_file_count}</td>
                              <td className="p-3 text-sm text-emerald-700 font-medium">{item.matches_count}</td>
                              <td className="p-3 text-sm text-amber-700 font-medium">{item.ddn_only_count}</td>
                              <td className="p-3 text-sm text-red-700 font-medium">{item.dml_only_count}</td>
                              <td className="p-3 text-sm">
                                <Badge variant={item.match_percentage >= 80 ? "default" : item.match_percentage >= 50 ? "secondary" : "destructive"}>
                                  {item.match_percentage}%
                                </Badge>
                              </td>
                              <td className="p-3 text-sm">
                                <Button variant="outline" size="sm" onClick={(e) => exportDeliveryReport(item.id, e)} data-testid={`delivery-export-${item.id}`}>
                                  <Download className="w-3 h-3 mr-1" />Report
                                </Button>
                              </td>
                            </tr>
                            {expandedDelivery?.id === item.id && (
                              <tr>
                                <td colSpan={8} className="p-4 bg-slate-50">
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-4 gap-3">
                                      <div className="p-3 bg-slate-100 rounded-lg text-center">
                                        <p className="text-lg font-bold text-slate-900">{expandedDelivery.ddn_entries?.length || 0}</p>
                                        <p className="text-xs text-slate-600">DDN Entries</p>
                                      </div>
                                      <div className="p-3 bg-slate-100 rounded-lg text-center">
                                        <p className="text-lg font-bold text-slate-900">{expandedDelivery.dml_entries?.length || 0}</p>
                                        <p className="text-xs text-slate-600">DML Entries</p>
                                      </div>
                                      <div className="p-3 bg-emerald-100 rounded-lg text-center">
                                        <p className="text-lg font-bold text-emerald-700">{expandedDelivery.matches?.length || 0}</p>
                                        <p className="text-xs text-emerald-600">Matches</p>
                                      </div>
                                      <div className="p-3 bg-teal-100 rounded-lg text-center">
                                        <p className="text-lg font-bold text-teal-700">{expandedDelivery.match_percentage}%</p>
                                        <p className="text-xs text-teal-600">Match Rate</p>
                                      </div>
                                    </div>
                                    {expandedDelivery.in_ddn_only?.length > 0 && (
                                      <div>
                                        <p className="text-sm font-semibold text-amber-700 mb-1">In DDN Only ({expandedDelivery.in_ddn_only.length})</p>
                                        <div className="flex flex-wrap gap-1">
                                          {expandedDelivery.in_ddn_only.map((e, i) => <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-mono rounded">{e}</span>)}
                                        </div>
                                      </div>
                                    )}
                                    {expandedDelivery.in_dml_only?.length > 0 && (
                                      <div>
                                        <p className="text-sm font-semibold text-red-700 mb-1">In DML Only ({expandedDelivery.in_dml_only.length})</p>
                                        <div className="flex flex-wrap gap-1">
                                          {expandedDelivery.in_dml_only.map((e, i) => <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 text-xs font-mono rounded">{e}</span>)}
                                        </div>
                                      </div>
                                    )}
                                    {expandedDelivery.matches?.length > 0 && (
                                      <div>
                                        <p className="text-sm font-semibold text-emerald-700 mb-1">Matches ({expandedDelivery.matches.length})</p>
                                        <div className="flex flex-wrap gap-1">
                                          {expandedDelivery.matches.map((e, i) => <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-mono rounded">{e}</span>)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ═══════ TOOLS AND ACTIONS TAB ═══════ */}
      {activeTab === 'tools' && (
        <div className="space-y-6" data-testid="tab-content-tools">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {actionCards.map((card) => (
              <Card key={card.id} className="group relative overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer" onClick={card.onClick} data-testid={`project-card-${card.id}`}>
                <div className="aspect-[4/3] flex items-center justify-center p-5 bg-gradient-to-br from-slate-50 to-slate-100 group-hover:from-teal-50 group-hover:to-slate-50 transition-colors">
                  <img src={card.image} alt={card.title} className="w-full h-full object-contain max-w-[120px] max-h-[120px] drop-shadow-lg group-hover:scale-105 transition-transform duration-300" />
                </div>
                <CardHeader className="pb-2 pt-3">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {card.title}
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-teal-600" />
                  </CardTitle>
                  <CardDescription className="text-slate-500 text-sm">{card.desc}</CardDescription>
                </CardHeader>
                {card.count !== null && (
                  <CardContent className="pt-0 pb-4">
                    <span className="inline-flex items-center px-2.5 py-1 bg-teal-50 text-teal-700 text-sm font-medium rounded-md">{card.count} {card.countLabel}</span>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ═══════ HISTORY TAB ═══════ */}
      {activeTab === 'history' && (
        <div className="space-y-6" data-testid="tab-content-history">
          {/* BREX Builder History */}
          <Card className="border-slate-200" data-testid="brex-builder-history">
            <CardHeader className="cursor-pointer select-none" onClick={() => setBrexBuilderExpanded(!brexBuilderExpanded)}>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {brexBuilderExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                <FileText className="w-5 h-5 text-teal-600" />
                BREX Builder History
                <Badge variant="secondary" className="ml-2">{brexBuilderDocs.length}</Badge>
              </CardTitle>
              {!brexBuilderExpanded && <CardDescription>Click to view generated BREX Modules and Business Rules documents</CardDescription>}
            </CardHeader>
            {brexBuilderExpanded && (
              <CardContent>
                {brexBuilderDocs.length > 0 ? (
                  <div className="space-y-2">
                    {brexBuilderDocs.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`history-doc-${doc.id}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-teal-600 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-800">{doc.name}</p>
                            <p className="text-xs text-slate-500">{doc.description} &bull; {new Date(doc.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">{doc.doc_type === 'brdp_summary_pdf' ? 'PDF' : doc.doc_type}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-slate-500 py-4">No BREX Builder documents generated yet.</p>
                )}
              </CardContent>
            )}
          </Card>

          {/* DML Data Modules */}
          <Card className="border-slate-200" data-testid="project-dml-entries">
            <CardHeader className="cursor-pointer select-none" onClick={() => setDmlExpanded(!dmlExpanded)}>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {dmlExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                <FileText className="w-5 h-5 text-teal-600" />
                DML Data Modules
                <Badge variant="secondary" className="ml-2">{dmlEntries.length}</Badge>
              </CardTitle>
              {!dmlExpanded && <CardDescription>Click to view data modules in this project&apos;s DML</CardDescription>}
            </CardHeader>
            {dmlExpanded && (
              <CardContent className="space-y-2">
                {dmlEntries.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="project-dml-table">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data Module Code</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Schema</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Issue</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Security</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dmlEntries.map((entry, index) => (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`dml-entry-row-${index}`}>
                            <td className="p-3 text-sm font-mono text-teal-700">{entry.dmc_formatted}</td>
                            <td className="p-3 text-sm text-slate-700">{entry.title}</td>
                            <td className="p-3 text-sm text-slate-600">{entry.schema || '-'}</td>
                            <td className="p-3 text-sm text-slate-600">{entry.issue_number || '-'}</td>
                            <td className="p-3 text-sm text-slate-600">{entry.security_classification || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-sm text-slate-500 py-4">No data modules in this project&apos;s DML yet.</p>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Edit Project Modal */}
      <ProjectFormModal open={showEditModal} onOpenChange={setShowEditModal} onSuccess={() => fetchSummary()} project={project} />
    </div>
  );
};

export default ProjectDashboardPage;
